// packages/ui/src/components/kanban/KanbanCard.tsx
// Draggable card component for the Kanban board

import { useDraggable } from '@dnd-kit/core'
import type { DashboardCard } from '../../api/client'

const PRIORITY_COLORS: Record<DashboardCard['priority'], string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
}

const SOURCE_ICONS: Record<DashboardCard['source'], string> = {
  chat: '\u{1F4AC}',       // speech bubble
  cron: '\u{23F0}',        // alarm clock
  webhook: '\u{1F517}',    // link
  manual: '\u{270B}',      // raised hand
  discovery: '\u{1F50D}',  // magnifying glass
}

function getAge(createdAt: string): string {
  const diff = Date.now() - new Date(createdAt).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

export interface KanbanCardProps {
  card: DashboardCard
  onClick?: (card: DashboardCard) => void
}

export function KanbanCard({ card, onClick }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
  })

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`card-${card.id}`}
      className={`bg-gray-800 border border-gray-700 rounded-lg p-3 cursor-grab hover:border-gray-600 transition-colors ${
        isDragging ? 'opacity-50' : ''
      }`}
      onClick={() => onClick?.(card)}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-medium text-gray-100 leading-tight">
          {card.title}
        </h4>
        <span
          data-testid={`source-${card.source}`}
          className="text-xs shrink-0"
          title={card.source}
        >
          {SOURCE_ICONS[card.source] ?? '?'}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span
          data-testid={`priority-${card.priority}`}
          className={`text-xs px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[card.priority]}`}
        >
          {card.priority}
        </span>

        <span className="text-xs text-gray-500 ml-auto" title={card.createdAt}>
          {getAge(card.createdAt)}
        </span>
      </div>

      {card.targetHosts.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {card.targetHosts.map((host) => (
            <span
              key={host}
              className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded"
            >
              {host}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
